import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Plus, Trash2, ArrowLeft, AlertCircle, CheckCircle, BookOpen, Save, X } from "lucide-react";

const initialQ = { question: "", options: ["", "", "", ""], correct_index: 0, explanation: "", topic: "" };

export default function AdminQuestionsManager() {
    const { contestId } = useParams();
    const [contest, setContest] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(initialQ);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const load = async () => {
        try {
            const [c, q] = await Promise.all([
                api.get(`/contests/${contestId}`),
                api.get(`/admin/contests/${contestId}/questions`),
            ]);
            setContest(c.data);
            setQuestions(q.data.questions || []);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [contestId]); // eslint-disable-line

    const updateOpt = (i, v) => {
        const opts = [...form.options]; opts[i] = v; setForm({ ...form, options: opts });
    };

    const submitForm = async () => {
        setErr(""); setMsg("");
        if (!form.question.trim() || form.options.some((o) => !o.trim())) {
            setErr("Vui lòng nhập câu hỏi và đầy đủ 4 đáp án."); return;
        }
        setSaving(true);
        try {
            await api.post(`/admin/contests/${contestId}/questions`, form);
            setMsg("Đã thêm câu hỏi.");
            setForm(initialQ);
            setShowForm(false);
            await load();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (qid) => {
        if (!window.confirm("Xác nhận xoá câu hỏi này?")) return;
        try {
            await api.delete(`/admin/contests/${contestId}/questions/${qid}`);
            setMsg("Đã xoá câu hỏi.");
            setQuestions(questions.filter((q) => q.id !== qid));
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail));
        }
    };

    if (loading) return <div className="p-6 lg:p-10"><div className="card h-64 shimmer" /></div>;

    return (
        <div className="p-6 lg:p-10 max-w-6xl">
            <Link to="/quan-tri" className="btn-ghost mb-4" data-testid="back-admin"><ArrowLeft className="w-4 h-4" /> Về Quản trị</Link>
                <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] font-semibold flex items-center gap-2"><BookOpen className="w-3 h-3" /> Ngân hàng câu hỏi</div>
                        <h1 className="font-heading text-3xl font-bold mt-1">{contest?.title}</h1>
                        <p className="text-slate-600 text-sm mt-1">{questions.length} câu trắc nghiệm trong ngân hàng · Cần ≥ {contest?.num_mcq} câu để random</p>
                    </div>
                    <div className="flex gap-2">
                        <Link to={`/quan-tri/cuoc-thi/${contestId}`} className="btn-secondary" data-testid="edit-contest">Sửa cuộc thi</Link>
                        <button onClick={() => setShowForm(true)} className="btn-gold" data-testid="add-question-btn">
                            <Plus className="w-4 h-4" /> Thêm câu hỏi
                        </button>
                    </div>
                </div>

                {msg && <div className="text-emerald-700 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> {msg}</div>}
                {err && <div className="text-[var(--red)] text-sm bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {err}</div>}

                <div className="space-y-3">
                    {questions.length === 0 ? (
                        <div className="card p-10 text-center text-slate-500">
                            Chưa có câu hỏi. Bấm "Thêm câu hỏi" để bắt đầu.
                        </div>
                    ) : questions.map((q, idx) => (
                        <div key={q.id} className="card p-5" data-testid={`question-${idx}`}>
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="badge badge-gold">Câu {idx + 1}</span>
                                        {q.topic && <span className="badge badge-slate">{q.topic}</span>}
                                    </div>
                                    <h3 className="font-heading font-semibold text-[var(--navy)]">{q.question}</h3>
                                </div>
                                <button onClick={() => remove(q.id)} className="btn-ghost !text-[var(--red)]" data-testid={`delete-q-${q.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-2 mt-3">
                                {q.options.map((opt, i) => (
                                    <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg border text-sm ${
                                        i === q.correct_index ? "border-emerald-400 bg-emerald-50 text-emerald-900"
                                            : "border-[var(--border)] text-slate-700"}`}>
                                        <span className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                                            i === q.correct_index ? "bg-emerald-500 text-white" : "bg-[var(--bg-alt)] text-slate-600"
                                        }`}>{String.fromCharCode(65 + i)}</span>
                                        <span>{opt}</span>
                                    </div>
                                ))}
                            </div>
                            {q.explanation && (
                                <div className="mt-3 pt-3 border-t border-[var(--border)] text-xs text-slate-600">
                                    <strong className="text-[var(--gold-deep)]">Giải thích:</strong> {q.explanation}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            {/* Add Question Modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 bg-[var(--navy)]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                    <div className="card bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-5">
                            <h2 className="font-heading text-xl font-bold">Thêm câu hỏi trắc nghiệm</h2>
                            <button onClick={() => setShowForm(false)} className="btn-ghost"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="space-y-4">
                            <Field label="Câu hỏi" required>
                                <textarea className="input" rows={3} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="new-q-question" />
                            </Field>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">4 Đáp án — chọn đáp án đúng</label>
                                <div className="space-y-2">
                                    {form.options.map((opt, i) => (
                                        <label key={i} className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer ${
                                            form.correct_index === i ? "border-emerald-400 bg-emerald-50" : "border-[var(--border)]"
                                        }`}>
                                            <input type="radio" name="correct" checked={form.correct_index === i}
                                                onChange={() => setForm({ ...form, correct_index: i })}
                                                className="mt-2.5" data-testid={`new-q-correct-${i}`} />
                                            <span className="w-6 h-6 flex-shrink-0 rounded-full bg-[var(--bg-alt)] text-slate-600 flex items-center justify-center text-xs font-bold mt-1">{String.fromCharCode(65 + i)}</span>
                                            <input className="input flex-1 !p-2 text-sm" value={opt} onChange={(e) => updateOpt(i, e.target.value)} placeholder={`Đáp án ${String.fromCharCode(65 + i)}`} data-testid={`new-q-option-${i}`} />
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <Field label="Chủ đề (tùy chọn)">
                                <input className="input" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="VD: Luật Dân số" data-testid="new-q-topic" />
                            </Field>
                            <Field label="Giải thích (tùy chọn)">
                                <textarea className="input" rows={2} value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} data-testid="new-q-explanation" />
                            </Field>
                            {err && <div className="text-[var(--red)] text-sm bg-red-50 border border-red-200 rounded-lg p-2.5">{err}</div>}
                            <div className="flex justify-end gap-2 pt-3">
                                <button onClick={() => setShowForm(false)} className="btn-secondary">Hủy</button>
                                <button onClick={submitForm} disabled={saving} className="btn-gold" data-testid="save-question-btn">
                                    <Save className="w-4 h-4" /> {saving ? "Đang lưu…" : "Lưu câu hỏi"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
                {label} {required && <span className="text-[var(--red)]">*</span>}
            </label>
            {children}
        </div>
    );
}
